import React, { useCallback } from 'react';
import { X, Info, Keyboard } from 'lucide-react';
import { useGlobalKeymap } from '../../hooks/useGlobalKeymap';

const KEYBINDS = [
  { combo: 'Mouse click', action: 'Place selected object' },
  { combo: 'Esc', action: 'Cancel placement / close modals' },
  { combo: ',', action: 'Flip object horizontally while placing' },
  { combo: '.', action: 'Flip object horizontally while placing' },
  { combo: 'Delete/Backspace', action: 'Remove a placed object (click it)' },
  { combo: 'Q', action: 'Rotate map counterclockwise' },
  { combo: 'E', action: 'Rotate map clockwise' },
  { combo: 'North button', action: 'Reset compass to North' }
];

const InfoPanel = ({ onClose, showInfo = true }) => {
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose && onClose();
    }
  }, [onClose]);

  useGlobalKeymap([
    showInfo ? {
      key: 'Escape',
      onEvent: (e) => { try { e.preventDefault(); } catch (_) {} onClose && onClose(); },
      preventDefault: true,
      priority: 90,
      stop: true
    } : null
  ]);

  if (!showInfo) return null;

  return (
    <div className="fixed inset-0 z-[10000]">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-5xl bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-blue-50 dark:bg-gray-800">
            <div className="flex items-center space-x-2">
              <Info className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-blue-900 dark:text-blue-300">About NYC Public Space Event Stager</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-md text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-gray-700"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-6 py-5 space-y-6">
            <p className="text-sm text-gray-700 dark:text-gray-200">
              SpaceStager is a professional site planning tool optimized for NYC Open Streets, SAPO, and Parks event permits. Place equipment, annotate the map, and export a permit-ready site plan.
            </p>

            <div>
              <div className="flex items-center space-x-2 mb-3">
                <Keyboard className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">Keybindings</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {KEYBINDS.map((k) => (
                  <div key={k.combo} className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md px-3 py-2">
                    <span className="text-sm font-mono text-gray-800 dark:text-gray-100">{k.combo}</span>
                    <span className="text-sm text-gray-600 dark:text-gray-300">{k.action}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="text-sm text-gray-700 dark:text-gray-200 pt-12">
                Designed by <a href="https://www.mfranchi.net" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Matt Franchi</a> while a PiTech Doctoral Fellow at the Design Trust for Public Space, as part of the <a href="https://www.designtrust.org/projects/untaped/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Untaped Project</a>.
              </p>
            </div>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex items-center justify-end">
            <button onClick={onClose} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InfoPanel;
